import { useState, useEffect, useRef } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '@/auth/hooks/useAuth';
import api, { ArcheMetadata } from '@/lib/api';
import { trackArcheOpenOnce } from '@/arche/lib/track-arche-open';

interface ConditionalProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * A conditional route protection component for arches.
 *
 * - Public arches (isPublic=true) can be accessed without authentication
 * - Non-public arches require authentication
 *
 * This component fetches arche metadata to determine if the arche is public
 * before deciding whether to require authentication.
 */
export function ConditionalProtectedRoute({ children }: ConditionalProtectedRouteProps) {
  const { id: archeId } = useParams<{ id: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const location = useLocation();

  const [archeMetadata, setArcheMetadata] = useState<ArcheMetadata | null>(null);
  const [metadataLoading, setMetadataLoading] = useState(true);
  const [metadataError, setMetadataError] = useState<string | null>(null);

  // Track which archeId the current metadata belongs to.
  // This prevents stale metadata from triggering the slug redirect
  // during the render before the useEffect resets state.
  const metadataForArcheIdRef = useRef<string | null>(null);

  useEffect(() => {
    setMetadataLoading(true);
    setMetadataError(null);
    setArcheMetadata(null);
    metadataForArcheIdRef.current = null;

    if (!archeId) {
      setMetadataLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchMetadata() {
      try {
        const metadata = await api.getArcheMetadata(archeId!);
        if (!cancelled) {
          setArcheMetadata(metadata);
          setMetadataError(null);
        }
      } catch (err) {
        if (!cancelled) {
          // If we get a 404, treat it as not found
          // If we get a 401, the arche exists but requires auth
          setMetadataError(err instanceof Error ? err.message : 'Failed to load arche');
          setArcheMetadata(null);
        }
      } finally {
        if (!cancelled) {
          // Record that this archeId has been resolved (success or failure) so the
          // stale check below releases. Without this, an errored fetch would leave
          // metadataForArcheIdRef null and strand the component on "Loading…".
          metadataForArcheIdRef.current = archeId!;
          setMetadataLoading(false);
        }
      }
    }

    fetchMetadata();

    return () => {
      cancelled = true;
    };
  }, [archeId]);

  // Metadata is stale if it was fetched for a different archeId.
  // This check is synchronous (ref, not state) so it catches the render
  // that happens before the useEffect resets state.
  const metadataIsStale = metadataForArcheIdRef.current !== archeId;

  // Increment the arche's "Times Used" counter on successful open. Deduped
  // per tab-session (client) and per user-arche 30-min window (server).
  // Only fires when the user will actually see the arche — skips the slug→
  // archId redirect and the login-bounce paths.
  useEffect(() => {
    if (!archeMetadata || metadataIsStale) return;
    const willRedirectSlugToArchId =
      archeMetadata.archId !== archeId && archeMetadata.archId.startsWith('arche_');
    if (willRedirectSlugToArchId) return;
    if (!archeMetadata.isPublic && !isAuthenticated) return;
    trackArcheOpenOnce(archeMetadata.archId);
  }, [archeMetadata, archeId, isAuthenticated, metadataIsStale]);

  // Show loading while auth or metadata is being fetched
  if (authLoading || metadataLoading || metadataIsStale) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // No arche ID provided
  if (!archeId) {
    return <Navigate to="/" replace />;
  }

  // Redirect slug URLs to canonical arche ID URLs for internal arches only.
  // Internal arches (arche_*) need the archId for ArcheInteractionPage's dispatch chain.
  // Plugin arches (arc_*) keep the friendly slug URL — GenericArchePage resolves via metadata.
  if (
    archeMetadata &&
    archeMetadata.archId !== archeId &&
    archeMetadata.archId.startsWith('arche_')
  ) {
    const subPath = location.pathname.replace(`/a/${archeId}`, '');
    return <Navigate to={`/a/${archeMetadata.archId}${subPath}`} replace />;
  }

  // Metadata fetch failed - could be 404 or network error
  if (metadataError) {
    // If not authenticated, redirect to login (the arche might exist but require auth)
    if (!isAuthenticated) {
      return <Navigate to="/auth/login" state={{ from: location.pathname }} replace />;
    }
    // If authenticated but still got an error, show not found
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground">Not Found</h1>
          <p className="mt-2 text-muted-foreground">This arche could not be found.</p>
        </div>
      </div>
    );
  }

  // If arche is public, allow access regardless of auth state
  if (archeMetadata?.isPublic) {
    return <>{children}</>;
  }

  // Non-public arche requires authentication
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" state={{ from: location.pathname }} replace />;
  }

  return <>{children}</>;
}
